import {
  SignInPage as LegacySignInPage,
  type SignInProviderConfig,
  UserIdentity,
} from '@backstage/core-components';
import {
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * How long the app waits for the sign-in sequence before showing the provider
 * card anyway. Long enough that a slow redirect or a user reading the popup is
 * not interrupted, short enough that a page which never navigates does not stay
 * blank.
 */
const STUCK_TIMEOUT_MS = 15_000;

const microsoftProvider: SignInProviderConfig = {
  id: 'microsoft',
  title: 'Microsoft Entra ID',
  message: 'Inloggen met je NS-account',
  apiRef: microsoftAuthApiRef,
};

/**
 * The providers this app offers, in the order they are shown. The list is the
 * source of truth for how sign-in behaves: exactly one provider means there is
 * nothing to choose, so the sign-in sequence starts immediately.
 */
function offeredProviders(showGuest: boolean) {
  return showGuest
    ? (['guest', microsoftProvider] as const)
    : ([microsoftProvider] as const);
}

export function SignInPageComponent(props: SignInPageProps) {
  const config = useApi(configApiRef);
  const showGuest = config.getOptionalBoolean('ns.signIn.enableGuest') ?? false;
  const providers = offeredProviders(showGuest);

  // Escape hatch: turns the automatic start off without a deploy, leaving the
  // provider card with a working button. It can only ever disable the automatic
  // start, never enable it for a multi-provider list, so the two keys cannot
  // contradict each other.
  const autoSignInDisabled =
    config.getOptionalBoolean('ns.signIn.autoSignIn') === false;

  const only = providers.length === 1 ? providers[0] : undefined;

  if (only !== undefined && !autoSignInDisabled) {
    return <AutoSignIn {...props} provider={only} />;
  }

  // More than one provider, or the escape hatch is pulled: there is a choice to
  // make, so render the sign-in page.
  return <LegacySignInPage {...props} auto providers={[...providers]} />;
}

/**
 * Starts the sign-in sequence itself and renders nothing while it runs, so no
 * sign-in page appears. Backstage's own SingleSignInPage cannot do this: it
 * shows the provider card before starting, which is the click we are removing.
 *
 * With enableExperimentalRedirectFlow the sequence is a full-page redirect to
 * Entra, so this component unmounts with the navigation. Without it the
 * sequence is a popup and this component stays mounted until it resolves.
 */
function AutoSignIn({
  onSignInSuccess,
  provider,
}: SignInPageProps & { provider: SignInProviderConfig }) {
  const authApi = useApi(provider.apiRef);
  const [searchParams] = useSearchParams();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  // A failed sign-in returns here with ?error=, and starting again would send
  // the user straight back out: a redirect loop with no way to reach the app.
  // The error parameter is what breaks that cycle, so it is checked before any
  // attempt is made. Backstage's SingleSignInPage guards the same way.
  const failedEarlier = searchParams.get('error') !== null;
  const showCard = failed || failedEarlier;

  useEffect(() => {
    // Signing in opens a popup or navigates the page away, so starting twice
    // means two popups or two navigations. A ref survives re-runs of this
    // effect, where a local flag would not: it makes at-most-once a property of
    // the component rather than of assumptions about which dependencies change.
    if (started.current || showCard) {
      return undefined;
    }
    started.current = true;

    let cancelled = false;

    const signIn = async () => {
      try {
        const response = await authApi.getBackstageIdentity({
          instantPopup: true,
        });
        if (!response) {
          throw new Error(`${provider.title} returned no identity`);
        }
        if (!cancelled) {
          onSignInSuccess(
            UserIdentity.create({ identity: response.identity, authApi }),
          );
        }
      } catch {
        // Falling back to the card keeps a failure recoverable: the user gets a
        // button to retry rather than a page that never resolves.
        if (!cancelled) {
          setFailed(true);
        }
      }
    };

    signIn();

    // The redirect flow returns a promise that never settles, because the page
    // is expected to navigate away. If that navigation does not happen, nothing
    // rejects and this component would render an empty page indefinitely. The
    // card is shown instead, so there is always a way forward. It does not
    // cancel the attempt in flight: a redirect that arrives later still wins.
    const stuck = setTimeout(() => {
      if (!cancelled) {
        setFailed(true);
      }
    }, STUCK_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(stuck);
    };
  }, [authApi, onSignInSuccess, provider.title, showCard]);

  if (showCard) {
    return (
      <LegacySignInPage
        onSignInSuccess={onSignInSuccess}
        provider={provider}
      />
    );
  }

  return null;
}
